import { useState, useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { useFeatureFlag } from '@/contexts/FeatureFlagContext';
import { mlbApi } from '@/services/api';
import type { FeedbackSubmission } from '@/types/feedback';

interface BetaFeedbackWidgetProps {
  isOpen: boolean;
  onOpen: () => void;
  onClose: () => void;
  prefillSource?: string | null;
}

const WIDGET_CATEGORIES = [
  { id: 'bug', icon: '🐛', label: 'Bug', desc: "Something isn't working" },
  { id: 'suggestion', icon: '💡', label: 'Idea', desc: 'Feature request or improvement' },
  { id: 'confusing', icon: '🤔', label: 'Confusing', desc: 'Something was unclear' },
  { id: 'love', icon: '⚾', label: 'Love it', desc: 'Something you enjoyed' },
] as const;

type CategoryId = (typeof WIDGET_CATEGORIES)[number]['id'];

export function BetaFeedbackWidget({
  isOpen,
  onOpen,
  onClose,
  prefillSource = null,
}: BetaFeedbackWidgetProps) {
  const location = useLocation();
  const currentPage = location.pathname;

  const widgetEnabled = useFeatureFlag('beta_feedback_widget_enabled');

  const [category, setCategory] = useState<CategoryId | null>(null);
  const [message, setMessage] = useState('');
  const [email, setEmail] = useState('');
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const drawerRef = useRef<HTMLDivElement>(null);
  const firstFocusableRef = useRef<HTMLButtonElement>(null);

  // Reset form state 300ms after closing (for animation)
  useEffect(() => {
    if (!isOpen) {
      const timer = setTimeout(() => {
        setCategory(null);
        setMessage('');
        setEmail('');
        setSubmitted(false);
        setError(null);
      }, 300);
      return () => clearTimeout(timer);
    }
  }, [isOpen]);

  // Focus trap: focus first element when drawer opens
  useEffect(() => {
    if (isOpen && firstFocusableRef.current) {
      firstFocusableRef.current.focus();
    }
  }, [isOpen]);

  // Close on Escape key
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && isOpen) {
        onClose();
      }
    };
    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [isOpen, onClose]);

  const handleSubmit = async () => {
    if (!category || !message.trim()) return;

    setError(null);
    setSubmitting(true);

    try {
      const submission: FeedbackSubmission = {
        submission_type: 'beta',
        category,
        source: prefillSource || 'widget',
        message: message.trim(),
        page_url: currentPage,
        email: email.trim() || undefined,
      };

      await mlbApi.submitFeedback(submission);
      setSubmitted(true);
    } catch {
      setError('Failed to submit feedback. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  // Format source label: remove "nugget_" prefix, replace "_" with " ", capitalize
  const formatSourceLabel = (source: string) => {
    return source
      .replace(/^nugget_/, '')
      .replace(/_/g, ' ')
      .split(' ')
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');
  };

  if (!widgetEnabled) return null;

  // ── FLOATING TAB (when closed) ──────────────────────────
  if (!isOpen) {
    return (
      <button
        onClick={onOpen}
        className="fixed z-[1000] flex items-center gap-1.5 bg-ink border border-[rgba(139,115,85,0.4)] cursor-pointer
          right-4 bottom-20 sm:right-0 sm:bottom-auto sm:top-1/2 sm:-translate-y-1/2
          rounded-lg sm:rounded-l-md sm:rounded-r-none
          px-4 py-2.5 sm:px-2 sm:py-3
          shadow-[0_2px_12px_rgba(0,0,0,0.15)] sm:shadow-[-2px_0_8px_rgba(0,0,0,0.1)]
          sm:[writing-mode:vertical-rl] sm:[text-orientation:mixed]
          border-r sm:border-r-0"
        aria-label="Open feedback widget"
      >
        <span className="text-sm sm:hidden">💬</span>
        <span className="font-sans text-[11px] font-bold tracking-[1.5px] uppercase text-gold">
          Feedback
        </span>
      </button>
    );
  }

  // ── SLIDE-OUT DRAWER ────────────────────────────────────
  return (
    <>
      {/* Backdrop */}
      <div
        onClick={onClose}
        className="fixed inset-0 bg-[rgba(44,36,22,0.4)] backdrop-blur-[2px] z-[1000]"
        aria-hidden="true"
      />

      {/* Drawer */}
      <div
        ref={drawerRef}
        className="fixed right-0 top-0 bottom-0 w-full sm:w-[360px] bg-white
          border-l-0 sm:border-l sm:border-sand
          shadow-[-4px_0_20px_rgba(0,0,0,0.1)]
          z-[1001] flex flex-col overflow-hidden"
        role="dialog"
        aria-modal="true"
        aria-labelledby="feedback-drawer-title"
      >
        {/* Header */}
        <div className="bg-ink px-4 py-3.5 sm:py-3.5 flex justify-between items-center flex-shrink-0">
          <div>
            <h2 id="feedback-drawer-title" className="font-display text-base font-black text-linen">
              Beta Feedback
            </h2>
            <p className="font-serif text-[11px] text-warm-gray-light italic mt-0.5">
              Help us build the press box right.
            </p>
          </div>
          <button
            ref={firstFocusableRef}
            onClick={onClose}
            className="bg-transparent border-none text-warm-gray text-[22px] cursor-pointer leading-none px-1"
            aria-label="Close feedback drawer"
          >
            ×
          </button>
        </div>

        {/* ── SUBMITTED ─────────────────────────────────── */}
        {submitted ? (
          <div className="flex-1 flex flex-col items-center justify-center px-8 py-8 text-center">
            <div className="text-4xl mb-3">⚾</div>
            <div className="font-display text-xl font-black text-ink mb-1.5">Thanks, scorer.</div>
            <div className="font-serif text-[13px] text-espresso italic leading-relaxed mb-5">
              Your feedback has been logged. We read every submission.
            </div>
            <button
              onClick={onClose}
              className="bg-ink border-none font-sans text-xs font-bold text-linen
                px-6 py-2.5 rounded cursor-pointer tracking-[0.5px]"
            >
              Close
            </button>
          </div>
        ) : (
          <>
            {/* ── FORM BODY ───────────────────────────────── */}
            <div className="flex-1 overflow-auto px-4 py-4">
              {/* Context chip */}
              <div
                className="flex items-center gap-1.5 mb-4 px-2.5 py-1.5
                bg-cream-light border border-sand rounded flex-wrap"
              >
                <span className="font-sans text-[9px] font-bold tracking-wider uppercase text-warm-gray">
                  PAGE
                </span>
                <span className="font-mono text-[11px] text-espresso">{currentPage}</span>
                {prefillSource && (
                  <>
                    <span className="text-sand">·</span>
                    <span className="font-sans text-[9px] font-bold tracking-wider uppercase text-warm-gray">
                      VIA
                    </span>
                    <span className="font-sans text-[10px] font-semibold text-gold">
                      {formatSourceLabel(prefillSource)}
                    </span>
                  </>
                )}
              </div>

              {/* Category selector */}
              <div className="font-sans text-[10px] font-bold tracking-[2px] uppercase text-warm-gray mb-2">
                What kind of feedback?
              </div>
              <div className="grid grid-cols-2 gap-2 mb-4">
                {WIDGET_CATEGORIES.map((cat) => (
                  <button
                    key={cat.id}
                    onClick={() => setCategory(cat.id)}
                    className={`${
                      category === cat.id
                        ? 'bg-ink border-ink text-linen'
                        : 'bg-white border-sand text-ink'
                    } border rounded-md px-2.5 py-2.5 cursor-pointer text-left transition-all duration-150`}
                  >
                    <div className="text-base mb-1">{cat.icon}</div>
                    <div className="font-sans text-xs font-bold mb-0.5">{cat.label}</div>
                    <div
                      className={`font-serif text-[10px] ${
                        category === cat.id ? 'text-warm-gray-light' : 'text-warm-gray'
                      }`}
                    >
                      {cat.desc}
                    </div>
                  </button>
                ))}
              </div>

              {/* Message */}
              <label
                htmlFor="feedback-message"
                className="block font-sans text-[10px] font-bold tracking-[2px] uppercase text-warm-gray mb-2"
              >
                Tell us more
              </label>
              <textarea
                id="feedback-message"
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                placeholder="What happened? What did you expect?"
                rows={4}
                className="w-full px-3 py-3 border border-sand rounded-md
                  font-serif text-[13px] text-ink bg-cream-light
                  resize-vertical leading-relaxed box-border"
                required
              />

              {/* Optional email */}
              <label
                htmlFor="feedback-email"
                className="block font-serif text-[11px] text-warm-gray-light italic mt-2.5 mb-1"
              >
                Want us to follow up? (optional)
              </label>
              <input
                id="feedback-email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="your@email.com"
                className="w-full px-3 py-2 border border-sand rounded
                  font-sans text-xs text-ink bg-cream-light box-border"
              />
            </div>

            {/* Error message */}
            {error && (
              <div
                className="mx-4 mb-2 px-3 py-2 bg-red-50 border border-red-200 rounded text-[12px] font-serif text-red-600"
                role="alert"
                aria-live="assertive"
              >
                {error}
              </div>
            )}

            {/* Submit footer */}
            <div className="px-4 py-3 border-t border-sand bg-cream-light flex-shrink-0">
              <button
                onClick={handleSubmit}
                disabled={!category || !message.trim() || submitting}
                className={`w-full border-none font-sans text-[13px] font-bold
                  px-5 py-3 rounded cursor-pointer tracking-wider uppercase
                  transition-all duration-150
                  ${
                    category && message.trim() && !submitting
                      ? 'bg-ink text-linen cursor-pointer'
                      : 'bg-sand text-warm-gray cursor-default'
                  }`}
              >
                {submitting ? 'Submitting...' : 'Submit Feedback'}
              </button>
            </div>
          </>
        )}
      </div>
    </>
  );
}
